import React, { useCallback, useRef, useState } from 'react';
import {
  CarouselBasic,
  CarouselBasicWithNav,
  CarouselCategories,
  CarouselCentered,
  CarouselSingle,
  CarouselVisualNav,
  CarouselWithThumbs,
  CategoryOutfits,
  CarouselOutfits,
  CarouselWithHtml,
  RenderModal,
} from './subcomponents';

import { CarouselBasicProps } from './subcomponents/CarouselBasic/CarouselBasic';
import { CarouselBasicWithNavProps } from './subcomponents/CarouselBasicWithNav/CarouselBasicWithNav';
import { CarouselCategoriesProps } from './subcomponents/CarouselCategories/CarouselCategories';
import { CategoryOutfitsProps } from './subcomponents/CategoryOutfits/CategoryOutfits';
import { CarouselCenteredProps } from './subcomponents/CarouselCentered/CarouselCentered';
import { CarouselSingleProps } from './subcomponents/CarouselSingle/CarouselSingle';
import { CarouselVisualNavProps } from './subcomponents/CarouselVisualNav/CarouselVisualNav';
import { CarouselWithThumbsProps } from './subcomponents/CarouselWithThumbs/CarouselWithThumbs';
import { CarouselWithHtmlProps } from './subcomponents/CarouselWithHtml/CarouselWithHtml';
let instanceCounter = 0;

import 'pure-react-carousel/dist/react-carousel.es.css';
import { IconV2IconNames } from '../IconV2/IconV2';

export interface CarouselCards {
  /** This text helps to describe the URL to CTA */
  ctaText?: string;
  ctaUrl?: string;
  filterId?: string;
  filterValue: IconV2IconNames;
  id: string;
  imageAltText?: string;
  imageDescription?: string;
  /** The URL for the image */
  imagePath: string;
  imageTitle?: string;
  /** A value for href in a link. Clicking on an image brings you to this URL */
  imageUrl: string;
  isDisabled?: boolean;
  isSelectedFacet?: boolean;
  type?: 'single' | 'double' | 'triple';
  videoPath?: string;
}

export interface FirstCard {
  description?: null | string;
  image?: null | string;
  imageAlt?: null | string;
  links?:
    | null
    | {
        linkText?: string;
        linkUrl?: string;
        linkTextColor?: string;
      }[];
  sizes?: null | string;
  title?: null | string;
}

export type VideoPlayingState = {
  id?: string;
  videoState?: string;
};

/**
 * Some props related to React Modal require other props need to be defined or not defined.
 * For example: The setAppElement prop is required by React Modal when the modal prop is true.  Read
 * the React Modal documentation for more information.
 * Another Example: if the modal props is true, you must omit the handleOnKey and handleOnClick
 * props
 **/
type ModalProps =
  | {
      modal?: false | undefined | never;
      setAppElement?: never;
    }
  | {
      modal: true;
      setAppElement: string | HTMLElement;
      /**
       * For most modes, pressing space or enter while focused on a slide will open that slide in a
       * modal window.  However, if you supply your own value for handleKeyEvent, the default behavior
       * is short-circuited and only your action will run.
       */
      handleOnKey?: never;
      /**
       * For most modes, clicking on a slide will open that slide in a modal window.  However, if you
       * supply your own value for handleOnClick, the default behavior is short-circuited and only your
       * action will run.
       */
      handleOnClick?: never;
    };

interface CommonProps {
  /**
   * For most modes, pressing space or enter while focused on a slide will open that slide in a
   * modal window.  However, if you supply your own value for handleKeyEvent, the default behavior
   * is short-circuited and only your action will run.
   */
  handleKeyEvent?: (i: number, ev: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * For most modes, clicking on a slide will open that slide in a modal window.  However, if you
   * supply your own value for handleOnClick, the default behavior is short-circuited and only your
   * action will run.
   */
  handleOnClick?: (
    i: number | string,
    ev: React.MouseEvent<HTMLElement>
  ) => void;
  /**
   * A string representing one of the 12 display modes for this component. The names are somewhat
   * self-explanatory.  For more clarification, see the examples in Storybook
   */
  mode?:
    | 'auto'
    | 'basic-with-nav'
    | 'basic'
    | 'categories'
    | 'category-outfits'
    | 'centered'
    | 'outfits'
    | 'single'
    | 'visualNav'
    | 'with-bottom-thumbs'
    | 'with-html'
    | 'with-right-thumbs'
    | 'with-thumbs'
    | 'with-top-thumbs';
  visibleSlides?: number;
}

type BasicMode = { mode: 'basic' } & CarouselBasicProps & ModalProps;
type BasicWithNavMode = { mode: 'basic-with-nav' } & CarouselBasicWithNavProps &
  ModalProps;
type CategoriesMode = { mode: 'categories' } & CarouselCategoriesProps;
type CategoryOutfitsMode = { mode: 'category-outfits' } & CategoryOutfitsProps;
type CenteredMode = { mode: 'centered' } & CarouselCenteredProps & ModalProps;
type OutfitsMode = { mode: 'outfits' } & CategoryOutfitsProps;
type SingleMode = { mode: 'single' } & CarouselSingleProps & ModalProps;
type VisualNavMode = { mode: 'visualNav' } & CarouselVisualNavProps;
type CarouselWithBottomThumbsMode = {
  mode: 'with-bottom-thumbs';
} & CarouselWithThumbsProps &
  ModalProps;
type CarouselWithHtmlMode = { mode: 'with-html' } & CarouselWithHtmlProps;
type CarouselWithRightThumbsMode = {
  mode: 'with-right-thumbs';
} & CarouselWithThumbsProps &
  ModalProps;
type CarouselWithLeftThumbsMode = {
  mode: 'with-thumbs';
} & CarouselWithThumbsProps &
  ModalProps;
type CarouselWithTopThumbsMode = {
  mode: 'with-top-thumbs';
} & CarouselWithThumbsProps &
  ModalProps;
type AutoModeOneSlide = {
  mode: 'auto';
  mainImages?: [string]; // exactly one image
} & CarouselSingleProps &
  ModalProps;
type AutoModeTwoSlides = {
  mode: 'auto';
  mainImages?: [string, string]; // exactly two images
} & CarouselBasicProps &
  ModalProps;
type AutoModeMoreThanTwoSlides = {
  mode: 'auto';
  mainImages?: [string, string, string, ...string[]]; // three or more images
} & CarouselWithThumbsProps &
  ModalProps;

type AutoMode =
  | AutoModeOneSlide
  | AutoModeTwoSlides
  | AutoModeMoreThanTwoSlides;

type ConditionalPropsDeterminedByMode =
  | AutoMode
  | BasicMode
  | BasicWithNavMode
  | CategoriesMode
  | CategoryOutfitsMode
  | CenteredMode
  | OutfitsMode
  | SingleMode
  | VisualNavMode
  | CarouselWithBottomThumbsMode
  | CarouselWithHtmlMode
  | CarouselWithRightThumbsMode
  | CarouselWithLeftThumbsMode
  | CarouselWithTopThumbsMode;

export type CarouselProps = CommonProps & ConditionalPropsDeterminedByMode;

/**
 * Carousel is a giant container component orchestrating various types of Pure React Carousel
 * implementations. If you are creating a new feature, instead of using this carousel, first
 * consider doing the following...
 * 1) Try to leverage Pure React Carousel directly in your new feature. No need to add the overhead of this component to your new feature if you can help it.
 * 2) If your feature is a mobile-first feature, try simply placing an HTML div element inside of another parent div that has overflow set and css scroll snap style defined.

 *
 * All of the Carousels have been updated. Many now use functional components. If the need arises,
 * feel free to use the more basic types of carousel modes:
 * - auto
 * - basic
 * - basic-with-nav
 * - centered
 * - single
 * - carousel-with-thumbs
 * - carousel-with-bottom-thumbs
 * - carousel-with-top-thumbs
 * - carousel-with-right-thumbs
 *
 * However, the following modes are deprecated and are only being maintained because they are the
 * fall-back if a flag is turned off. They really should never have been created here in the first
 * place since they are very specialized only to category (aka PLP) pages. Do not use these in new
 * code. Do not adapt or update these deprecated modes.
 * - categories
 * - category-outfits
 * - visualNav
 *
 *
 */
const Carousel = (props: CarouselProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalImageIndex, setModalImageIndex] = useState<number | null>(null);

  const uid = useRef<number>(-1);

  if (uid.current < 0) {
    uid.current = instanceCounter;
    instanceCounter += 1;
  }

  const { handleKeyEvent, handleOnClick, mode } = props;

  const keyEventCallback = useCallback(
    (i: number, ev: React.KeyboardEvent<HTMLElement>) => {
      if (handleKeyEvent) {
        ev.persist();
        handleKeyEvent(i, ev);
        // short-circuit the callback.  Don't update state. Don't open modal.
        return;
      }
      const { key } = ev;
      if (key === 'Enter' || key === ' ') {
        // open the modal
        ev.preventDefault();
        setIsModalOpen(true);
        setModalImageIndex(i);
      }
    },
    [handleKeyEvent]
  );

  const mouseEventCallback = useCallback(
    (i: number | string, ev: React.MouseEvent<HTMLElement>) => {
      if (handleOnClick) {
        ev.persist();
        handleOnClick(i, ev);
        // short-circuit the callback.  Don't update state. Don't open modal.
        return;
      }
      if (typeof i === 'number') {
        // open the modal
        setIsModalOpen(true);
        setModalImageIndex(i);
      }
    },
    [handleOnClick]
  );

  const handleOnModalClose = useCallback(() => {
    setIsModalOpen(false);
    if (typeof modalImageIndex === 'number') {
      const slides = document.querySelectorAll<HTMLElement>(
        `[data-uid="${uid.current}"] .focus-on-modal-close`
      );
      slides[modalImageIndex].focus();
    }
    setModalImageIndex(null);
  }, [modalImageIndex]);

  function renderCarousel() {
    // IF the developer is using Typescript, they will never reach the final else statement in this
    // very long if-else chain. Typescript won't even compile unless the developer uses a valid
    // "mode" property for <Carousel />.  Only vanilla JavaScript developers can see this error if
    // they use an invalid value for "mode".
    /* istanbul ignore else */
    if (mode === 'auto') {
      const { mainImages = [] } = props;
      if ((mainImages.length || 0) == 1) {
        // Single
        return (
          <CarouselSingle
            {...props}
            isModalOpen={isModalOpen}
            modalImageIndex={modalImageIndex}
            uid={uid.current}
          />
        );
      } else if (mainImages.length === 2) {
        // Basic
        return (
          <CarouselBasic
            {...props}
            isModalOpen={isModalOpen}
            modalImageIndex={modalImageIndex}
            onClick={mouseEventCallback}
            onKeyDown={keyEventCallback}
            uid={uid.current}
          />
        );
      } else {
        // With-Thumbs (Thumbs are on the left by default)
        return (
          <CarouselWithThumbs
            {...(props as CarouselWithThumbsProps)}
            isModalOpen={isModalOpen}
            modalImageIndex={modalImageIndex}
            onClick={mouseEventCallback}
            onKeyDown={keyEventCallback}
            uid={uid.current}
          />
        );
      }
    } else if (mode === 'basic') {
      return (
        <CarouselBasic
          {...props}
          isModalOpen={isModalOpen}
          modalImageIndex={modalImageIndex}
          onClick={mouseEventCallback}
          onKeyDown={keyEventCallback}
          uid={uid.current}
        />
      );
    } else if (mode === 'basic-with-nav') {
      return (
        <CarouselBasicWithNav
          {...props}
          isModalOpen={isModalOpen}
          modalImageIndex={modalImageIndex}
          onClick={mouseEventCallback}
          onKeyDown={keyEventCallback}
          uid={uid.current}
        />
      );
    } else if (mode === 'categories') {
      return (
        <CarouselCategories
          {...props}
          onClick={mouseEventCallback}
          uid={uid.current}
        />
      );
    } else if (mode === 'category-outfits') {
      return (
        <CategoryOutfits
          {...props}
          onClick={mouseEventCallback}
          onKeyDown={keyEventCallback}
          uid={uid.current}
        />
      );
    } else if (mode === 'centered') {
      return (
        <CarouselCentered
          {...props}
          isModalOpen={isModalOpen}
          modalImageIndex={modalImageIndex}
          onClick={mouseEventCallback}
          onKeyDown={keyEventCallback}
          uid={uid.current}
        />
      );
    } else if (mode === 'outfits') {
      return (
        <CarouselOutfits
          {...props}
          onClick={mouseEventCallback}
          onKeyDown={keyEventCallback}
          uid={uid.current}
        />
      );
    } else if (mode === 'single') {
      return (
        <CarouselSingle
          {...props}
          isModalOpen={isModalOpen}
          modalImageIndex={modalImageIndex}
          onClick={mouseEventCallback}
          onKeyDown={keyEventCallback}
          uid={uid.current}
        />
      );
    } else if (mode === 'visualNav') {
      return (
        <CarouselVisualNav
          {...props}
          onClick={mouseEventCallback}
          uid={uid.current}
        />
      );
    } else if (mode === 'with-bottom-thumbs') {
      return (
        <CarouselWithThumbs
          {...props}
          isModalOpen={isModalOpen}
          modalImageIndex={modalImageIndex}
          onClick={mouseEventCallback}
          onKeyDown={keyEventCallback}
          thumbnailMode="bottom"
          uid={uid.current}
        />
      );
    } else if (mode === 'with-thumbs') {
      return (
        <CarouselWithThumbs
          {...props}
          isModalOpen={isModalOpen}
          modalImageIndex={modalImageIndex}
          onClick={mouseEventCallback}
          onKeyDown={keyEventCallback}
          uid={uid.current}
        />
      );
    } else if (mode === 'with-html') {
      return <CarouselWithHtml {...props} uid={uid.current} />;
    } else if (mode === 'with-right-thumbs') {
      return (
        <CarouselWithThumbs
          {...props}
          isModalOpen={isModalOpen}
          modalImageIndex={modalImageIndex}
          onClick={mouseEventCallback}
          onKeyDown={keyEventCallback}
          thumbnailMode="right"
          uid={uid.current}
        />
      );
    } else if (mode === 'with-top-thumbs') {
      return (
        <CarouselWithThumbs
          {...props}
          isModalOpen={isModalOpen}
          modalImageIndex={modalImageIndex}
          onClick={mouseEventCallback}
          onKeyDown={keyEventCallback}
          thumbnailMode="top"
          uid={uid.current}
        />
      );
    } else {
      // Istanbul coverage for this else block is being ignored. See the comment at very beginning
      // of the if-else block for an explanation.
      throw new Error(
        'Illegal Carousel mode.  Must be one of "auto", "basic", "center", "single", ' +
          '"visualNav", "with-thumbs", "with-top-thumbs", "with-right-thumbs", "with-bottom-thumbs", "with-html", ' +
          '"outfits", "category-outfits" or left blank to default to "auto"'
      );
    }
  }

  function renderModal() {
    const { mode } = props;
    if (
      (mode === 'basic' ||
        mode === 'basic-with-nav' ||
        mode === 'centered' ||
        mode === 'single' ||
        mode === 'with-thumbs' ||
        mode === 'with-bottom-thumbs' ||
        mode === 'with-right-thumbs' ||
        mode === 'with-top-thumbs' ||
        mode === 'auto') &&
      props.modal &&
      isModalOpen
    ) {
      return (
        <RenderModal
          handleOnModalClose={handleOnModalClose}
          modalImageIndex={modalImageIndex}
          {...props}
        />
      );
    }
  }

  return (
    <>
      {
        // Render the correct type of carousel based on carousel mode
        renderCarousel()
      }
      {
        // Render an optional React Modal component.
        renderModal()
      }
    </>
  );
};

Carousel.displayName = 'Carousel';

export default Carousel;
